import React from 'react';
import { Box, Avatar, Typography, Stack } from '@mui/material';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import LanguageIcon from '@mui/icons-material/Language';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import TelegramIcon from '@mui/icons-material/Telegram';
import PinterestIcon from '@mui/icons-material/Pinterest';
import RedditIcon from '@mui/icons-material/Reddit';
import GitHubIcon from '@mui/icons-material/GitHub';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
// Brand logos MUI doesn't ship — sourced from Simple Icons via react-icons (already a dependency).
import { SiTiktok, SiSnapchat, SiThreads, SiSpotify, SiShopify, SiX, SiPatreon, SiDiscord,
    SiOnlyfans, SiTwitch, SiMedium, SiSubstack, SiBehance, SiDribbble, SiSoundcloud, SiVimeo,
    SiQuora, SiMastodon, SiKofi, SiLinktree } from 'react-icons/si';
import { BIO_THEMES, BUTTON_SHAPES } from '../../../../core/services/bio.service';

// Popular platforms offered as suggestions in the social-icon dropdown (editable/freeSolo).
export const SOCIAL_PLATFORMS = [
    'Instagram', 'YouTube', 'Facebook', 'X', 'LinkedIn', 'TikTok', 'WhatsApp',
    'Telegram', 'Pinterest', 'Snapchat', 'Reddit', 'Threads', 'Spotify', 'Shopify',
    'GitHub', 'Discord', 'Patreon', 'Email', 'Website',
];

// Shared icon resolver — known platforms get their logo, anything else gets a generic link icon.
export const socialIcon = (platform: string) => {
    switch ((platform || '').toLowerCase().trim()) {
        case 'instagram': return <InstagramIcon fontSize="small" />;
        case 'youtube': return <YouTubeIcon fontSize="small" />;
        case 'facebook': return <FacebookIcon fontSize="small" />;
        case 'linkedin': return <LinkedInIcon fontSize="small" />;
        case 'twitter': return <TwitterIcon fontSize="small" />;
        case 'x': return <SiX size={17} />;
        case 'tiktok': return <SiTiktok size={17} />;
        case 'whatsapp': return <WhatsAppIcon fontSize="small" />;
        case 'telegram': return <TelegramIcon fontSize="small" />;
        case 'pinterest': return <PinterestIcon fontSize="small" />;
        case 'snapchat': return <SiSnapchat size={17} />;
        case 'reddit': return <RedditIcon fontSize="small" />;
        case 'threads': return <SiThreads size={17} />;
        case 'spotify': return <SiSpotify size={17} />;
        case 'shopify': return <SiShopify size={17} />;
        case 'discord': return <SiDiscord size={17} />;
        case 'patreon': return <SiPatreon size={17} />;
        case 'github': return <GitHubIcon fontSize="small" />;
        case 'email': case 'mail': return <MailOutlineIcon fontSize="small" />;
        // Renowned platforms beyond the suggestion list — auto-matched when typed as a custom name.
        case 'onlyfans': return <SiOnlyfans size={17} />;
        case 'twitch': return <SiTwitch size={17} />;
        case 'medium': return <SiMedium size={17} />;
        case 'substack': return <SiSubstack size={17} />;
        case 'behance': return <SiBehance size={17} />;
        case 'dribbble': return <SiDribbble size={17} />;
        case 'soundcloud': return <SiSoundcloud size={17} />;
        case 'vimeo': return <SiVimeo size={17} />;
        case 'quora': return <SiQuora size={17} />;
        case 'mastodon': return <SiMastodon size={17} />;
        case 'ko-fi': case 'kofi': return <SiKofi size={17} />;
        case 'linktree': return <SiLinktree size={17} />;
        default: return <LanguageIcon fontSize="small" />;
    }
};

// A link block whose title is just the pasted URL ("https://www.flipkart.com/...?pid=...")
// reads badly on a button — show "flipkart.com" instead. Real titles pass through.
export const linkLabel = (title?: string | null): string => {
    const t = (title || '').trim();
    if (!t) return 'Link';
    const host = t.match(/^(?:https?:\/\/)?(?:www\.)?([^/?#\s]+)/i);
    return /^(https?:\/\/|www\.)\S+$/i.test(t) && host ? host[1] : t;
};

const parseJson = (v: any, fallback: any) => {
    if (!v) return fallback;
    if (typeof v === 'object') return v;
    try { return JSON.parse(v); } catch { return fallback; }
};

interface Props {
    page: any;
    blocks: any[];
    // Builds the href for a link block; return '' for a non-clickable preview.
    hrefFor?: (block: any) => string;
}

// Presentational link-in-bio page — shared by the public page and the editor preview.
export const BioRender: React.FC<Props> = ({ page, blocks, hrefFor }) => {
    const theme = { ...BIO_THEMES.light, ...parseJson(page?.theme, {}) };
    const shape = BUTTON_SHAPES[(parseJson(page?.theme, {})?.button_shape) || 'rounded'] ?? 12;
    const socials: any[] = parseJson(page?.social_links, []) || [];

    const bgGradient = `radial-gradient(ellipse 120% 40% at 50% 0%, ${theme.accent}1a 0%, ${theme.bg} 65%)`;

    return (
        <Box sx={{
            background: bgGradient,
            color: theme.text,
            minHeight: '100%',
            px: 2.5,
            py: { xs: 2, md: 4 },
            textAlign: 'center',
        }}>
            {/* Avatar with accent ring */}
            <Box sx={{
                width: 100, height: 100,
                borderRadius: '50%',
                background: `linear-gradient(135deg, ${theme.accent} 0%, ${theme.accent}88 100%)`,
                p: '3px',
                mx: 'auto',
                mb: 2,
                mt: { xs: 0, sm: 2 },
            }}>
                <Avatar
                    src={page?.avatar_url || undefined}
                    sx={{
                        width: '100%', height: '100%',
                        bgcolor: theme.bg,
                        color: theme.accent,
                        fontSize: 36,
                        fontWeight: 700,
                        border: `2.5px solid ${theme.bg}`,
                    }}
                >
                    {/* {(page?.display_name || '?')[0]} 123 */}
                    {(page?.display_name || '?')[0]}
                </Avatar>
            </Box>

            <Typography variant="h5" fontWeight={800} sx={{ color: theme.text, letterSpacing: '-0.4px', lineHeight: 1.2 }}>
                {page?.display_name || 'Your name'}
            </Typography>

            {page?.headline && (
                <Typography variant="body2" sx={{
                    color: theme.text, opacity: 0.6,
                    mt: 0.75, mb: 0.5,
                    maxWidth: 300, mx: 'auto',
                    lineHeight: 1.6,
                }}>
                    {page.headline}
                </Typography>
            )}

            {/* Social icons as circular ghost buttons */}
            {socials.length > 0 && (
                <Stack direction="row" justifyContent="center" sx={{ mt: 2, mb: 3, flexWrap: 'wrap', gap: 1 }}>
                    {socials.filter((s: any) => s?.url).map((s: any, i: number) => (
                        <a key={i} href={s.url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                            <Box sx={{
                                width: 38, height: 38,
                                borderRadius: '50%',
                                border: `1.5px solid ${theme.accent}44`,
                                bgcolor: `${theme.accent}0d`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                color: theme.accent,
                                transition: 'all 0.18s ease',
                                '&:hover': {
                                    bgcolor: theme.accent,
                                    color: theme.buttonText,
                                    borderColor: theme.accent,
                                    transform: 'scale(1.12)',
                                    boxShadow: `0 4px 12px ${theme.accent}44`,
                                },
                            }}>
                                {socialIcon(s.platform)}
                            </Box>
                        </a>
                    ))}
                </Stack>
            )}

            {/* Blocks */}
            <Stack spacing={1.5} sx={{ mt: socials.length > 0 ? 0 : 3, maxWidth: '100%', mx: 'auto' }}>
                {blocks.filter(b => b.is_active === undefined || b.is_active === 1 || b.is_active === true).map((b) => {
                    if (b.block_type === 'divider') {
                        const h = (parseJson(b.config, {})?.height) || 'small';
                        const my = h === 'large' ? 3 : h === 'medium' ? 1.75 : 0.75;
                        return (
                            <Box key={b.block_id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, my }}>
                                <Box sx={{ flex: 1, height: '1px', bgcolor: `${theme.text}18` }} />
                                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: `${theme.text}28` }} />
                                <Box sx={{ flex: 1, height: '1px', bgcolor: `${theme.text}18` }} />
                            </Box>
                        );
                    }
                    if (b.block_type === 'title') {
                        if (!b.title?.trim()) return null;
                        return (
                            <Typography key={b.block_id} variant="overline" fontWeight={700}
                                sx={{ color: theme.text, opacity: 0.45, letterSpacing: 1.8, display: 'block', mt: 1 }}>
                                {b.title}
                            </Typography>
                        );
                    }
                    if (b.block_type === 'text') {
                        if (!b.title?.trim()) return null;
                        return (
                            <Typography key={b.block_id} variant="body2"
                                sx={{ color: theme.text, opacity: 0.72, whiteSpace: 'pre-wrap', lineHeight: 1.65, px: 0.5 }}>
                                {b.title}
                            </Typography>
                        );
                    }
                    // link button
                    const href = hrefFor ? hrefFor(b) : '';
                    const inner = (
                        <Box sx={{
                            bgcolor: theme.accent,
                            color: theme.buttonText,
                            borderRadius: `${shape}px`,
                            px: 2, py: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            boxShadow: `0 2px 10px ${theme.accent}30`,
                            transition: 'all 0.2s ease',
                            position: 'relative',
                            overflow: 'hidden',
                            '&::after': {
                                content: '""',
                                position: 'absolute',
                                inset: 0,
                                background: 'linear-gradient(135deg, rgba(255,255,255,0.13) 0%, transparent 55%)',
                                pointerEvents: 'none',
                                borderRadius: 'inherit',
                            },
                            '&:hover': href ? {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 6px 20px ${theme.accent}45`,
                            } : {},
                        }}>
                            {b.image_url ? (
                                <Box component="img" src={b.image_url} alt=""
                                    style={{ width: 38, height: 38, borderRadius: shape > 8 ? 8 : 3, objectFit: 'cover', flexShrink: 0 }} />
                            ) : null}
                            {/* minWidth 0 lets the title shrink inside the flex row; long
                                words break and anything past two lines is ellipsized, so a
                                long title can never push out of the button. */}
                            <Typography variant="body2" fontWeight={700}
                                sx={{
                                    flexGrow: 1, minWidth: 0, textAlign: 'center', letterSpacing: 0.1, fontSize: '0.875rem',
                                    overflowWrap: 'anywhere', wordBreak: 'break-word',
                                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                }}>
                                {linkLabel(b.title)}
                            </Typography>
                            {/* Arrow is part of the link-button design — always show it
                                (incl. the editor preview, which has no real href) so the
                                preview matches the live /bio page exactly. */}
                            <Box sx={{ width: b.image_url ? 20 : 18, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', opacity: 0.65 }}>
                                <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                                    <path d="M2.5 7.5h10M8.5 3.5l4 4-4 4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </Box>
                        </Box>
                    );
                    return href ? (
                        <a key={b.block_id} href={href} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>{inner}</a>
                    ) : (
                        <Box key={b.block_id} sx={{ cursor: 'default' }}>{inner}</Box>
                    );
                })}
            </Stack>

            {/* Footer */}
            <Typography variant="caption" sx={{
                color: theme.text, opacity: 0.3,
                display: 'block', mt: 6,
                fontWeight: 500, fontSize: '0.68rem', letterSpacing: 0.4,
            }}>
                Powered by ClanConnect
            </Typography>
        </Box>
    );
};
